import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import { getPurposesMasterDataAsync, Purpose } from './purposeSlice';

interface PurposeParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    width50: {
      width: '50%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

export default function PurposeView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();

  const sortOptions = [
    { value: 'id', text: t('purposeView.sortOptions.createdDate') },
    { value: 'abbreviation', text: t('purposeView.sortOptions.name') }
  ];
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { masterData: purposes, count } = useSelector(
    (state: RootState) => state.purposes
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedPurpose, setSelectedPurpose] = useState<Purpose | null>(null);
  const [purposeParams, setPurposeParams] = useState<PurposeParamProps>({
    page: 0,
    limit: 30,
    sortBy: 'id',
    orderBy: ORDER.asc
  });

  useEffect(() => {
    loadingPurpose(purposeParams);
  }, []);

  function loadingPurpose(params: PurposeParamProps) {
    dispatch(getPurposesMasterDataAsync(params));
    setPurposeParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingPurpose({
      ...purposeParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, purpose?: Purpose) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && purpose) {
        setSelectedPurpose(purpose);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={`${selectedPurpose?.abbreviation} | ${selectedPurpose?.fullName}`}
              />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`purposes/${selectedPurpose?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('masterData.purpose.actions.edit')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('masterData.purpose.actions.delete')}
                secondary={t('projectCard.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('masterData.purpose.title'),
          masterDataRef: 'purposes'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('masterData.purpose.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('masterData.purpose.title')}
        params={purposeParams}
        sortOptions={sortOptions}
        loadingElement={loadingPurpose}
      />
      {purposes.map((purpose) => {
        return (
          <Card className={classes.card} key={purpose.id}>
            <div className={classes.cardContainer}>
              <div className={classes.width50}>
                <Typography>{`${purpose.abbreviation} | ${purpose.fullName}`}</Typography>
              </div>
              <div className={classes.width50}>
                <Typography>{purpose.phase}</Typography>
              </div>
            </div>
            <div>
              <IconButton size="small" onClick={toggleDrawer(true, purpose)}>
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / purposeParams.limit)}
          shape="rounded"
          page={purposeParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`purposes/create`}
          variant="extended"
          color="primary"
          aria-label={t('masterData.purpose.actions.newAria') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('masterData.purpose.actions.new')}
        </Fab>
      </Zoom>
    </div>
  );
}
